import React, { useState, useEffect } from 'react';
import axios from 'axios'; // Import axios
import DataTable from 'react-data-table-component';
import { Box, Typography, FormControl, Select, MenuItem, TextField, FormControlLabel } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { Switch, styled, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: 'green',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: 'red',
    opacity: 1,
  },
}));

const MyTable = () => {
  const [data, setData] = useState([]); // Initial empty data state
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(true); // State to track loading
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      const allIds = data.map(item => item.id);
      setSelectedRows(allIds);
    }
    setSelectAll(!selectAll);
  };
  
  const handleRowCheckboxChange = (rowId) => {
    if (selectedRows.includes(rowId)) {
      setSelectedRows(selectedRows.filter(id => id !== rowId));
    } else {
      setSelectedRows([...selectedRows, rowId]);
    }
  };

  useEffect(() => {
    setLoading(true); // Start loading
    axios.get(`${process.env.REACT_APP_API_URL}/items/allitems`)
      .then(response => {
        if (response.data && response.data.items) {
          const transformedData = response.data.items.map(item => {
            const price = item.itemDetails.price || 0;
            const discount = item.itemDetails.discount || 0;
            const discountedPrice = price - (price * (discount / 100)); // Calculate discount based on percentage

            return {
              id: item._id,
              image: item.itemDetails.mediaFiles[0]?.url || '', 
              itemName: item.itemDetails.name || 'No Name', 
              price: discountedPrice, 
              originalPrice: price, // Show the price after discount
              publish: item.itemDetails.publish || false, 
              category: item.itemDetails.category?.name || 'No Category', 
              brand: item.itemDetails.brand?.title || 'No Brand', 
            };
          });
          setData(transformedData);
        }
      })
      .catch(error => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        setLoading(false); // Stop loading
      });
  }, []);

  
  const getItemById = async (itemId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/items/itembyid/${itemId}`);
      if (response.status === 200 && response.data) {
        const itemName = response.data?.product?.itemDetails?.name || 'No Name'; 
        console.log('Item Name:', itemName); 
        return itemName;
      } else {
        console.error('Failed to fetch item by ID');
        return null;
      }
    } catch (error) {
      console.error('Error fetching item by ID:', error);
      return null;  
    }
  };

  const deleteItem = async (itemId) => {
    try {
      const itemName = await getItemById(itemId);
      if (itemName) {
        const response = await axios.delete(`${process.env.REACT_APP_API_URL}/items/delete/${itemId}`);
        if (response.status === 200) {
          setData(prevData => prevData.filter(item => item.id !== itemId));
          toast.success(`${itemName} has been deleted.`);
        } else {
          toast.error('Failed to delete item.');
        }
      } else {
        toast.error('Item not found, could not delete.');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item.');
    }
  };
  const handlePublishToggle = async (itemId, currentPublishStatus, rowData) => {
    const updatedPublishStatus = !currentPublishStatus; // Toggle the publish status
  
    // Create a new FormData object
    const formData = new FormData();
  
    // Append the dynamic data fields
    formData.append('itemDetails[categoryName]', rowData.category || '');  // Use dynamic category from rowData
    formData.append('itemDetails[brandName]', rowData.brand || '');        // Use dynamic brand from rowData
    formData.append('itemDetails[publish]', updatedPublishStatus.toString()); // Toggle the publish status
  
    try {
      // Make PUT request with the FormData
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/items/update/${itemId}
        `,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',  // This tells the server to expect form data
          },
        }
      );
  
      if (response.status === 200) {
        // Update the local data with the new publish status
        setData(prevData =>
          prevData.map(item =>
            item.id === itemId ? { ...item, publish: updatedPublishStatus } : item
          )
        );
        toast.success(`Item has been ${updatedPublishStatus ? 'published' : 'unpublished'}.`);
      } else {
        toast.error('Failed to update publish status.');
      }
    } catch (error) {
      console.error('Error updating publish status:', error);
      toast.error('Failed to update publish status.');
    }
  };
  
 
  
  

  const navigate = useNavigate();
  const columns = [
    {
      name: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} checked={selectAll} onChange={handleSelectAll}>
          <Box>
            <input type="checkbox" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'green' }}>
            <Delete
              sx={{
                cursor: 'pointer',
                padding: '0px',
                fontSize: '18px',
              }}
              onClick={() => alert('Delete all items action triggered!')}
            />
            <Typography variant="body1">All</Typography>
          </Box>
        </Box>
      ),
      selector: row => row.id,
      cell: (row) => (
        <input type="checkbox"  checked={selectedRows.includes(row.id)} 
        onChange={() => handleRowCheckboxChange(row.id)} />
      ),
      sortable: false,
      style: {
        padding: '8px',
        textAlign: 'center',
      },
    },
    {
      name: 'Images',
      selector: row => row.image,
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={`${row.image}`}
            alt={row.itemName}
            style={{ width: 50, height: 50, objectFit: 'cover', marginRight: 8 }}
          />
        </Box>
      ),
      sortable: false,
      style: {
        padding: '8px',
      },
    },
    {
      name: 'Name',
      selector: row => row.itemName,
      sortable: true,
      style: {
        padding: '8px',
      },
    },
    {
      name: 'Price',
      selector: row => row.price,
      sortable: true,
      cell: row => (
        <Typography>
          ₹{row.price}
          <span style={{ textDecoration: 'line-through', color: 'red', marginLeft: '8px' }}>
          ₹{row.originalPrice}
          </span>
        </Typography>
      ),
      style: {
        padding: '8px',
      },
    },
    {
      name: 'Category',
      selector: row => row.category,
      sortable: true,
      style: {
        padding: '8px',
      },
    },
    {
      name: 'Brand',
      selector: row => row.brand,
      sortable: true,
      style: {
        padding: '8px',
      },
    },
    {
      name: 'Publish',
      selector: row => row.publish,
      cell: row => (
        <FormControlLabel
          control={
            <IOSSwitch
              checked={row.publish}
              onChange={() => handlePublishToggle(row.id, row.publish, row)}
            />
          }
        />
      ),
      sortable: false,
      style: {
        padding: '8px',
      },
    },
    {
      name: 'Actions',
      cell: row => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Edit
            sx={{
              cursor: 'pointer',
              backgroundColor: 'green',
              color: '#fff',
              borderRadius: '50%',
              padding: '5px',
              fontSize: '28px',
            }}
            onClick={() => navigate(`edit/${row.id}`)}
          />
          <Delete
            sx={{
              cursor: 'pointer',
              backgroundColor: 'red',
              color: '#fff',
              borderRadius: '50%',
              padding: '6px',
              fontSize: '28px',
            }}
            onClick={() => deleteItem(row.id)}
          />
        </Box>
      ),
      sortable: false,
      style: {
        padding: '8px',
      },
    },
  ];

  const filteredData = data.filter(item =>
    item.id.toString().includes(searchTerm) ||
    item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.price.toString().includes(searchTerm) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (page - 1) * entriesPerPage;
  const currentPageData = filteredData.slice(startIndex, startIndex + entriesPerPage);

  const handleEntriesChange = event => {
    setEntriesPerPage(event.target.value);
    setPage(1); // Reset page to 1 when changing entries per page
  };

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px',gap: { xs: '10px', md: 0 }, // Apply gap on small screens
    flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1 }}>
            Show
          </Typography>
          <FormControl sx={{ p: 1 }}>
            <Select
              value={entriesPerPage}
              onChange={handleEntriesChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Entries per page' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body1" sx={{ mr: 1 }}>
            entries
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ mr: 1 }}>
            Search:
          </Typography>
          <TextField
            className="recent-or-search-cls"
            id="outlined-basic"
            label=""
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
          />
        </Box>
      </Box>

      
        <Box sx={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
        {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <CircularProgress />
           <Typography variant="body1" sx={{ marginLeft: 2 }}>Items loading...</Typography>
        </Box>
      ) : (
          <DataTable
            columns={columns}
            data={currentPageData}
            pagination
            paginationServer
            paginationTotalRows={filteredData.length}
            onChangePage={newPage => setPage(newPage)}
            paginationPerPage={entriesPerPage}
            paginationComponentOptions={{
              noRowsPerPage: true,
            }}
            customStyles={{
              table: {
                style: {
                  tableLayout: 'auto',
                },
              },
              cells: {
                style: {
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  color: 'green',
                  fontSize: '14px',
                },
              },
              headCells: {
                style: {
                  backgroundColor: '#f4f4f4',
                  fontWeight: 'bold',
                  padding: '8px',
                  border: '1px solid #dee2e6',
                  fontSize: '1rem',
                },
              },
              rows: {
                style: {
                  '&:hover': {
                    backgroundColor: '#f1f1f1',
                  },
                  '&:nth-child(odd)': {
                    boxShadow: 'inset 0 0 0 9999px rgba(0, 0, 0, 0.023)',
                  },
                },
              },
            }}
          />
        )}
        </Box>
    </div>
  );
};

export default MyTable;
